import React from "react"
import { Select } from "../select"

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]
export const DEFAULT_PAGE_SIZE = 25
const VISIBLE_PAGE_COUNT = 7 // Total pages to show in the window

/**
 * @typedef {Object} PaginationProps
 * @property {number} page - Current page (0-indexed)
 * @property {number} pageSize - Rows per page
 * @property {number} totalCount - Total row count
 * @property {function(number): void} onPageChange - Page change callback
 * @property {function(number): void} onPageSizeChange - Page size change callback
 */

/**
 * Get visible page numbers with ellipsis markers.
 * Uses a sliding window of VISIBLE_PAGE_COUNT pages, centered on current page
 * but clamped to edges. Always shows first/last page with ellipsis when needed.
 * @param {number} currentPage - Current page (0-indexed)
 * @param {number} totalPages - Total number of pages
 * @returns {Array<number|string>} Array of page numbers and "..." markers
 */
function getVisiblePages(currentPage, totalPages) {
  if (totalPages <= 1) return [0]

  // If total pages fit in the window, show all
  if (totalPages <= VISIBLE_PAGE_COUNT) {
    return Array.from({ length: totalPages }, (_, i) => i)
  }

  // Calculate window start/end, keeping current page centered when possible
  const halfWindow = Math.floor(VISIBLE_PAGE_COUNT / 2)
  let windowStart = currentPage - halfWindow
  let windowEnd = currentPage + halfWindow

  // Clamp to edges
  if (windowStart < 0) {
    windowStart = 0
    windowEnd = VISIBLE_PAGE_COUNT - 1
  }
  if (windowEnd >= totalPages) {
    windowEnd = totalPages - 1
    windowStart = totalPages - VISIBLE_PAGE_COUNT
  }

  const result = []

  // Add first page + ellipsis if window doesn't start at 0
  if (windowStart > 0) {
    result.push(0)
    if (windowStart > 1) {
      result.push("...")
    }
  }

  // Add pages in the window
  for (let i = windowStart; i <= windowEnd; i++) {
    result.push(i)
  }

  // Add ellipsis + last page if window doesn't end at last page
  if (windowEnd < totalPages - 1) {
    if (windowEnd < totalPages - 2) {
      result.push("...")
    }
    result.push(totalPages - 1)
  }

  return result
}

/**
 * Pagination controls component.
 * @param {PaginationProps} props
 */
export function Pagination({ page, pageSize, totalCount, onPageChange, onPageSizeChange }) {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  const visiblePages = getVisiblePages(page, totalPages)

  // Generate options for page dropdown
  const pageOptions = Array.from({ length: totalPages }, (_, i) => ({
    value: i,
    label: `Page ${i + 1}`
  }))

  const handlePageClick = (newPage) => {
    if (newPage !== page && newPage >= 0 && newPage < totalPages) {
      onPageChange(newPage)
    }
  }

  const handlePageSelect = (option) => {
    if (option && option.value !== page) {
      onPageChange(option.value)
    }
  }

  const handlePageSizeClick = (newSize) => {
    if (newSize !== pageSize) {
      onPageSizeChange(newSize)
    }
  }

  return (
    <div className="paginated-table-pagination">
      <div className="paginated-table-pagination__pages">
        <button
          type="button"
          className="paginated-table-pagination__nav"
          disabled={page === 0}
          onClick={() => handlePageClick(page - 1)}
          aria-label="Previous page"
        >
          &lt;
        </button>

        {visiblePages.map((p, idx) =>
          p === "..." ? (
            <span key={`ellipsis-${idx}`} className="paginated-table-pagination__ellipsis">
              ...
            </span>
          ) : (
            <button
              key={p}
              type="button"
              className={`paginated-table-pagination__page ${p === page ? "paginated-table-pagination__page--selected" : ""}`}
              aria-selected={p === page ? "true" : "false"}
              onClick={() => handlePageClick(p)}
            >
              {p + 1}
            </button>
          )
        )}

        <button
          type="button"
          className="paginated-table-pagination__nav"
          disabled={page === totalPages - 1}
          onClick={() => handlePageClick(page + 1)}
          aria-label="Next page"
        >
          &gt;
        </button>

        {totalPages > VISIBLE_PAGE_COUNT && (
          <Select
            className="paginated-table-pagination__select"
            options={pageOptions}
            value={pageOptions[page]}
            onChange={handlePageSelect}
            isSearchable={true}
            placeholder="Go to page..."
          />
        )}
      </div>

      <div className="paginated-table-pagination__sizes">
        {PAGE_SIZE_OPTIONS.map((size) => (
          <button
            key={size}
            type="button"
            className={`paginated-table-pagination__size ${size === pageSize ? "paginated-table-pagination__size--selected" : ""}`}
            aria-selected={size === pageSize ? "true" : "false"}
            onClick={() => handlePageSizeClick(size)}
          >
            {size}
          </button>
        ))}
      </div>
    </div>
  )
}
